<template>
  <div class="p-guest-info-form">
    <div class="p-guest-info-form__intro">
      <h2
        class="p-guest-info-form__title f-title-24-bold t-fg-primary"
        v-text="$t('chat.guest.title')"
      />
      <p class="f-body-14 t-fg-tertiary" v-text="$t('chat.guest.subtitle')" />
    </div>

    <div class="p-guest-info-form__fields">
      <TextField
        v-model="name"
        size="xlarge"
        type="text"
        :label="$t('chat.guest.namePlaceholder')"
        :placeholder="$t('chat.guest.namePlaceholder')"
        :disabled="loading"
        :clearable="false"
        autocomplete="name"
        name="guest-name"
      />

      <TextField
        v-model="email"
        size="xlarge"
        type="email"
        :label="$t('chat.guest.emailPlaceholder')"
        :placeholder="$t('chat.guest.emailPlaceholder')"
        :status="showEmailError ? 'error' : 'default'"
        :assistive-text="showEmailError ? $t('chat.guest.emailError') : undefined"
        :disabled="loading"
        :clearable="false"
        autocomplete="email"
        name="guest-email"
        @blur="emailTouched = true"
      />

      <TextField
        size="xlarge"
        type="tel"
        :model-value="phoneDisplay"
        :label="$t('chat.guest.phonePlaceholder')"
        :placeholder="$t('chat.guest.phonePlaceholder')"
        :status="showPhoneError ? 'error' : 'default'"
        :assistive-text="showPhoneError ? $t('chat.guest.phoneError') : undefined"
        :disabled="loading"
        :clearable="false"
        autocomplete="tel"
        name="guest-phone"
        @update:model-value="phone = normalizePhoneDigits($event)"
        @blur="phoneTouched = true"
      />

      <TextField
        size="xlarge"
        type="text"
        :model-value="tckn"
        :label="$t('chat.guest.tcknPlaceholder')"
        :placeholder="$t('chat.guest.tcknPlaceholder')"
        :status="showTcknError ? 'error' : 'default'"
        :assistive-text="showTcknError ? $t('chat.guest.tcknError') : $t('chat.guest.tcknHint')"
        :disabled="loading"
        :clearable="false"
        name="guest-tckn"
        @update:model-value="tckn = normalizeTckn($event)"
        @blur="tcknTouched = true"
      />
    </div>

    <p-button
      class="full-width"
      variant="filled"
      color="green"
      size="lg"
      :disabled="!isValid || loading"
      @click="handleSubmit"
    >
      {{ loading ? $t('chat.guest.submitting') : $t('chat.guest.submit') }}
    </p-button>

    <p v-if="error" class="f-utility-14 t-fg-danger" v-text="error" />
  </div>
</template>

<script setup lang="ts">
import { TextField } from '@p-utilities/vue'

interface Props {
  loading?: boolean
  error?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  error: null,
})

// `phone` is emitted as the national part without the trunk zero
// (e.g. `5346536990`) — the shape the helpdesk session endpoint expects.
// `tckn` is optional and emitted as an empty string when left blank.
const emit = defineEmits<{
  submit: [name: string, email: string, phone: string, tckn: string]
}>()

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Trunk zero + 10 national digits — same rule the dynamic support forms use.
const PHONE_REGEX = /^0\d{10}$/

const name = ref('')
const email = ref('')
// Stored as normalized digits (`0` + 10); the field renders `phoneDisplay`.
const phone = ref('')
const tckn = ref('')
const emailTouched = ref(false)
const phoneTouched = ref(false)
const tcknTouched = ref(false)

// Keep only digits, fold the +90 / 0090 prefixes into the trunk zero, and
// cap at 11 so the mask can't overflow. Mirrors PDynamicForm's phone input.
const normalizePhoneDigits = (s = '') => {
  let d = s.replace(/\D/g, '')
  if (d.startsWith('0090')) d = '0' + d.slice(4)
  else if (d.startsWith('90')) d = '0' + d.slice(2)
  if (d && d[0] !== '0') d = '0' + d
  return d.slice(0, 11)
}

// `0 (534) 653 69 90`, built progressively so partial input stays readable.
const formatPhoneDisplay = (raw = '') => {
  const d = normalizePhoneDigits(raw)
  if (!d) return ''

  const rest = d.startsWith('0') ? d.slice(1) : d
  let out = '0'

  if (rest.length) {
    out += ' (' + rest.slice(0, Math.min(3, rest.length))
    if (rest.length >= 3) out += ')'
  }
  if (rest.length > 3) out += ' ' + rest.slice(3, Math.min(6, rest.length))
  if (rest.length > 6) out += ' ' + rest.slice(6, Math.min(8, rest.length))
  if (rest.length > 8) out += ' ' + rest.slice(8, Math.min(10, rest.length))
  return out
}

const normalizeTckn = (s = '') => s.replace(/\D/g, '').slice(0, 11)

// Standard TCKN checksum: 11 digits, no leading zero, and the last two
// digits are check digits over the first nine. The backend re-validates.
const isValidTckn = (value: string): boolean => {
  if (!/^[1-9]\d{10}$/.test(value)) return false
  const d = value.split('').map(Number)
  const odd = d[0] + d[2] + d[4] + d[6] + d[8]
  const even = d[1] + d[3] + d[5] + d[7]
  if ((((odd * 7 - even) % 10) + 10) % 10 !== d[9]) return false
  const first10 = d.slice(0, 10).reduce((sum, n) => sum + n, 0)
  return first10 % 10 === d[10]
}

const phoneDisplay = computed(() => formatPhoneDisplay(phone.value))

const isEmailValid = computed(() => EMAIL_REGEX.test(email.value))
const isPhoneValid = computed(() => PHONE_REGEX.test(phone.value))
// Optional field: blank is valid, anything typed has to check out.
const isTcknValid = computed(() => tckn.value.length === 0 || isValidTckn(tckn.value))

const isValid = computed(
  () =>
    name.value.trim().length > 0 && isEmailValid.value && isPhoneValid.value && isTcknValid.value,
)

const showEmailError = computed(
  () => emailTouched.value && email.value.length > 0 && !isEmailValid.value,
)
const showPhoneError = computed(
  () => phoneTouched.value && phone.value.length > 0 && !isPhoneValid.value,
)
const showTcknError = computed(
  () => tcknTouched.value && tckn.value.length > 0 && !isTcknValid.value,
)

const handleSubmit = () => {
  if (!isValid.value || props.loading) return
  // Drop the trunk zero — the endpoint wants the bare national number.
  emit('submit', name.value.trim(), email.value, phone.value.slice(1), tckn.value)
}
</script>

<style lang="scss" scoped>
// Figma: guest-info container is 480×452; this form fills the body.
// Inputs use DS TextField (P26 native — `xlarge` size matches Figma's
// 56px-tall pill input). Error state is conveyed via `status="error"`
// and `assistiveText` (DS handles the error styling + helper-text slot).
.p-guest-info-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding-bottom: 24px;

  &__intro {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__title {
    margin: 0;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}
</style>
